import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
  AlignmentType,
  ShadingType,
  convertInchesToTwip,
} from "docx";
import { saveAs } from "file-saver";

interface ReportCriterion {
  title: string;
  subtitle: string;
  score: "yes" | "partially" | "no";
  assessment: string;
  tradeoff: { gain: string; giveUp: string };
  questions: string[];
}

export interface WordReportData {
  vendor: string;
  category: string;
  evaluationDate: string;
  version: string;
  executiveSummary: {
    keyTakeaway: string;
    sentiment: string;
    paragraphs: string[];
  };
  criteria: ReportCriterion[];
  keyQuestions: string[];
}

const COLORS = {
  darkGray: "2D3748",
  mediumGray: "4A5568",
  lightGray: "E2E8F0",
  white: "FFFFFF",
  green: "38A169",
  amber: "D69E2E",
  red: "E53E3E",
  headerBg: "2D3748",
};

function scoreLabel(score: string): string {
  switch (score) {
    case "yes":
      return "Pass";
    case "partially":
      return "Partial";
    case "no":
      return "Fail";
    default:
      return score;
  }
}

function scoreColor(score: string): string {
  switch (score) {
    case "yes":
      return COLORS.green;
    case "partially":
      return COLORS.amber;
    case "no":
      return COLORS.red;
    default:
      return COLORS.mediumGray;
  }
}

function buildTitleSection(data: WordReportData): Paragraph[] {
  return [
    new Paragraph({
      alignment: AlignmentType.LEFT,
      spacing: { after: 80 },
      children: [
        new TextRun({
          text: data.vendor,
          bold: true,
          size: 56,
          color: COLORS.darkGray,
          font: "Calibri",
        }),
      ],
    }),
    new Paragraph({
      spacing: { after: 40 },
      children: [
        new TextRun({
          text: "VENDOR EVALUATION REPORT",
          size: 20,
          color: COLORS.mediumGray,
          characterSpacing: 120,
          font: "Calibri",
        }),
      ],
    }),
    new Paragraph({
      spacing: { after: 40 },
      children: [
        new TextRun({
          text: data.category,
          size: 22,
          color: COLORS.mediumGray,
          font: "Calibri",
        }),
      ],
    }),
    new Paragraph({
      spacing: { after: 400 },
      children: [
        new TextRun({
          text: `Version ${data.version}  •  ${data.evaluationDate}`,
          size: 18,
          color: COLORS.mediumGray,
          font: "Calibri",
        }),
      ],
    }),
    // Horizontal rule
    new Paragraph({
      spacing: { after: 300 },
      border: {
        bottom: { style: BorderStyle.SINGLE, size: 6, color: COLORS.lightGray },
      },
    }),
  ];
}

function buildExecutiveSummary(data: WordReportData): Paragraph[] {
  const paragraphs: Paragraph[] = [
    new Paragraph({
      heading: HeadingLevel.HEADING_1,
      spacing: { before: 200, after: 200 },
      children: [
        new TextRun({
          text: "Executive Summary",
          bold: true,
          size: 36,
          color: COLORS.darkGray,
          font: "Calibri",
        }),
      ],
    }),
    new Paragraph({
      spacing: { after: 100 },
      children: [
        new TextRun({
          text: "KEY TAKEAWAY",
          bold: true,
          size: 18,
          color: COLORS.mediumGray,
          font: "Calibri",
        }),
      ],
    }),
    new Paragraph({
      spacing: { after: 240 },
      children: [
        new TextRun({
          text: data.executiveSummary.keyTakeaway,
          bold: true,
          size: 24,
          color: COLORS.darkGray,
          font: "Calibri",
        }),
      ],
    }),
  ];

  for (const p of data.executiveSummary.paragraphs) {
    paragraphs.push(
      new Paragraph({
        spacing: { after: 200 },
        children: [
          new TextRun({
            text: p,
            size: 22,
            color: COLORS.mediumGray,
            font: "Calibri",
          }),
        ],
      }),
    );
  }

  return paragraphs;
}

function buildScoreOverviewTable(criteria: ReportCriterion[]): (Paragraph | Table)[] {
  const headerRow = new TableRow({
    tableHeader: true,
    children: [
      new TableCell({
        shading: { type: ShadingType.SOLID, color: COLORS.headerBg },
        width: { size: 50, type: WidthType.PERCENTAGE },
        children: [
          new Paragraph({
            children: [
              new TextRun({ text: "Criterion", bold: true, size: 20, color: COLORS.white, font: "Calibri" }),
            ],
          }),
        ],
      }),
      new TableCell({
        shading: { type: ShadingType.SOLID, color: COLORS.headerBg },
        width: { size: 25, type: WidthType.PERCENTAGE },
        children: [
          new Paragraph({
            children: [
              new TextRun({ text: "Focus Area", bold: true, size: 20, color: COLORS.white, font: "Calibri" }),
            ],
          }),
        ],
      }),
      new TableCell({
        shading: { type: ShadingType.SOLID, color: COLORS.headerBg },
        width: { size: 25, type: WidthType.PERCENTAGE },
        children: [
          new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
              new TextRun({ text: "Result", bold: true, size: 20, color: COLORS.white, font: "Calibri" }),
            ],
          }),
        ],
      }),
    ],
  });

  const dataRows = criteria.map(
    (c, idx) =>
      new TableRow({
        children: [
          new TableCell({
            shading: {
              type: ShadingType.SOLID,
              color: idx % 2 === 0 ? COLORS.white : "F7FAFC",
            },
            children: [
              new Paragraph({
                children: [
                  new TextRun({ text: c.title, bold: true, size: 20, color: COLORS.darkGray, font: "Calibri" }),
                ],
              }),
            ],
          }),
          new TableCell({
            shading: {
              type: ShadingType.SOLID,
              color: idx % 2 === 0 ? COLORS.white : "F7FAFC",
            },
            children: [
              new Paragraph({
                children: [
                  new TextRun({ text: c.subtitle, size: 20, color: COLORS.mediumGray, font: "Calibri" }),
                ],
              }),
            ],
          }),
          new TableCell({
            shading: {
              type: ShadingType.SOLID,
              color: idx % 2 === 0 ? COLORS.white : "F7FAFC",
            },
            children: [
              new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                  new TextRun({
                    text: scoreLabel(c.score),
                    bold: true,
                    size: 20,
                    color: scoreColor(c.score),
                    font: "Calibri",
                  }),
                ],
              }),
            ],
          }),
        ],
      }),
  );

  return [
    new Paragraph({
      heading: HeadingLevel.HEADING_1,
      spacing: { before: 400, after: 200 },
      children: [
        new TextRun({
          text: "Evaluation Overview",
          bold: true,
          size: 36,
          color: COLORS.darkGray,
          font: "Calibri",
        }),
      ],
    }),
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [headerRow, ...dataRows],
    }),
  ];
}

function buildCriterionSection(criterion: ReportCriterion): Paragraph[] {
  const paragraphs: Paragraph[] = [
    new Paragraph({
      heading: HeadingLevel.HEADING_2,
      spacing: { before: 360, after: 80 },
      children: [
        new TextRun({
          text: `${criterion.title} — ${criterion.subtitle}`,
          bold: true,
          size: 28,
          color: COLORS.darkGray,
          font: "Calibri",
        }),
        new TextRun({ text: "   " }),
        new TextRun({
          text: `[${scoreLabel(criterion.score)}]`,
          bold: true,
          size: 22,
          color: scoreColor(criterion.score),
          font: "Calibri",
        }),
      ],
    }),
    // Assessment
    new Paragraph({
      spacing: { after: 200 },
      children: [
        new TextRun({
          text: criterion.assessment,
          size: 22,
          color: COLORS.mediumGray,
          font: "Calibri",
        }),
      ],
    }),
  ];

  // Trade-offs
  if (criterion.tradeoff.gain || criterion.tradeoff.giveUp) {
    paragraphs.push(
      new Paragraph({
        spacing: { before: 120, after: 60 },
        children: [
          new TextRun({
            text: "Trade-offs",
            bold: true,
            size: 22,
            color: COLORS.darkGray,
            font: "Calibri",
          }),
        ],
      }),
    );

    if (criterion.tradeoff.gain) {
      paragraphs.push(
        new Paragraph({
          spacing: { after: 60 },
          indent: { left: convertInchesToTwip(0.25) },
          children: [
            new TextRun({ text: "+ Gain: ", bold: true, size: 20, color: COLORS.green, font: "Calibri" }),
            new TextRun({ text: criterion.tradeoff.gain, size: 20, color: COLORS.mediumGray, font: "Calibri" }),
          ],
        }),
      );
    }

    if (criterion.tradeoff.giveUp) {
      paragraphs.push(
        new Paragraph({
          spacing: { after: 60 },
          indent: { left: convertInchesToTwip(0.25) },
          children: [
            new TextRun({ text: "− Give up: ", bold: true, size: 20, color: COLORS.red, font: "Calibri" }),
            new TextRun({ text: criterion.tradeoff.giveUp, size: 20, color: COLORS.mediumGray, font: "Calibri" }),
          ],
        }),
      );
    }
  }

  // Questions for vendor
  if (criterion.questions.length > 0) {
    paragraphs.push(
      new Paragraph({
        spacing: { before: 120, after: 60 },
        children: [
          new TextRun({
            text: "Questions for Vendor",
            bold: true,
            size: 22,
            color: COLORS.darkGray,
            font: "Calibri",
          }),
        ],
      }),
    );

    for (const q of criterion.questions) {
      paragraphs.push(
        new Paragraph({
          spacing: { after: 40 },
          indent: { left: convertInchesToTwip(0.25) },
          bullet: { level: 0 },
          children: [
            new TextRun({ text: q, size: 20, color: COLORS.mediumGray, font: "Calibri" }),
          ],
        }),
      );
    }
  }

  return paragraphs;
}

function buildTradeoffTable(criteria: ReportCriterion[]): (Paragraph | Table)[] {
  const headerRow = new TableRow({
    tableHeader: true,
    children: ["Criterion", "Result", "You Gain", "You Give Up"].map(
      (text) =>
        new TableCell({
          shading: { type: ShadingType.SOLID, color: COLORS.headerBg },
          children: [
            new Paragraph({
              children: [
                new TextRun({ text, bold: true, size: 18, color: COLORS.white, font: "Calibri" }),
              ],
            }),
          ],
        }),
    ),
  });

  const dataRows = criteria.map(
    (c, idx) =>
      new TableRow({
        children: [
          new TableCell({
            shading: { type: ShadingType.SOLID, color: idx % 2 === 0 ? COLORS.white : "F7FAFC" },
            children: [
              new Paragraph({
                children: [
                  new TextRun({ text: c.title, bold: true, size: 18, color: COLORS.darkGray, font: "Calibri" }),
                ],
              }),
            ],
          }),
          new TableCell({
            shading: { type: ShadingType.SOLID, color: idx % 2 === 0 ? COLORS.white : "F7FAFC" },
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: scoreLabel(c.score),
                    bold: true,
                    size: 18,
                    color: scoreColor(c.score),
                    font: "Calibri",
                  }),
                ],
              }),
            ],
          }),
          new TableCell({
            shading: { type: ShadingType.SOLID, color: idx % 2 === 0 ? COLORS.white : "F7FAFC" },
            children: [
              new Paragraph({
                children: [
                  new TextRun({ text: c.tradeoff.gain || "—", size: 18, color: COLORS.mediumGray, font: "Calibri" }),
                ],
              }),
            ],
          }),
          new TableCell({
            shading: { type: ShadingType.SOLID, color: idx % 2 === 0 ? COLORS.white : "F7FAFC" },
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: c.tradeoff.giveUp || "—",
                    size: 18,
                    color: COLORS.mediumGray,
                    font: "Calibri",
                  }),
                ],
              }),
            ],
          }),
        ],
      }),
  );

  return [
    new Paragraph({
      heading: HeadingLevel.HEADING_1,
      spacing: { before: 400, after: 200 },
      children: [
        new TextRun({
          text: "Trade-off Summary",
          bold: true,
          size: 36,
          color: COLORS.darkGray,
          font: "Calibri",
        }),
      ],
    }),
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [headerRow, ...dataRows],
    }),
  ];
}

function buildKeyQuestions(questions: string[]): Paragraph[] {
  const paragraphs: Paragraph[] = [
    new Paragraph({
      heading: HeadingLevel.HEADING_1,
      spacing: { before: 400, after: 200 },
      children: [
        new TextRun({
          text: "Key Questions for Your Decision",
          bold: true,
          size: 36,
          color: COLORS.darkGray,
          font: "Calibri",
        }),
      ],
    }),
    new Paragraph({
      spacing: { after: 160 },
      children: [
        new TextRun({
          text: "Consider these strategic questions based on your organizational context and priorities.",
          size: 22,
          color: COLORS.mediumGray,
          font: "Calibri",
        }),
      ],
    }),
  ];

  questions.forEach((q, idx) => {
    paragraphs.push(
      new Paragraph({
        spacing: { after: 120 },
        indent: { left: convertInchesToTwip(0.25) },
        children: [
          new TextRun({ text: `${idx + 1}. `, bold: true, size: 22, color: COLORS.darkGray, font: "Calibri" }),
          new TextRun({ text: q, size: 22, color: COLORS.mediumGray, font: "Calibri" }),
        ],
      }),
    );
  });

  return paragraphs;
}

function buildFooter(version: string): Paragraph[] {
  return [
    new Paragraph({
      spacing: { before: 600 },
      border: {
        top: { style: BorderStyle.SINGLE, size: 6, color: COLORS.lightGray },
      },
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 200, after: 60 },
      children: [
        new TextRun({
          text: "CONFIDENTIAL — FOR INTERNAL USE ONLY",
          size: 18,
          color: COLORS.mediumGray,
          characterSpacing: 60,
          font: "Calibri",
        }),
      ],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 60 },
      children: [
        new TextRun({
          text: "This evaluation assesses vendors on immediate utility and long-term organizational AI capacity building.",
          size: 18,
          color: COLORS.mediumGray,
          font: "Calibri",
        }),
      ],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [
        new TextRun({
          text: `Version ${version}`,
          size: 16,
          color: COLORS.mediumGray,
          font: "Calibri",
        }),
      ],
    }),
  ];
}

/**
 * Generates a Word (.docx) document from report data and triggers a download.
 * The exported document contains NO FeedForward branding.
 */
export async function downloadReportDocx(
  data: WordReportData,
  filename?: string,
): Promise<void> {
  const resolvedFilename =
    filename ??
    `${data.vendor.toLowerCase().replace(/\s+/g, "-")}-evaluation-report.docx`;

  const children: (Paragraph | Table)[] = [
    ...buildTitleSection(data),
    ...buildExecutiveSummary(data),
    ...buildScoreOverviewTable(data.criteria),
  ];

  // Detailed evaluation heading
  children.push(
    new Paragraph({
      heading: HeadingLevel.HEADING_1,
      spacing: { before: 400, after: 200 },
      children: [
        new TextRun({
          text: "Detailed Evaluation",
          bold: true,
          size: 36,
          color: COLORS.darkGray,
          font: "Calibri",
        }),
      ],
    }),
  );

  for (const criterion of data.criteria) {
    children.push(...buildCriterionSection(criterion));
  }

  children.push(...buildTradeoffTable(data.criteria));
  children.push(...buildKeyQuestions(data.keyQuestions));
  children.push(...buildFooter(data.version));

  const doc = new Document({
    styles: {
      default: {
        document: {
          run: { font: "Calibri", size: 22 },
        },
      },
    },
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: convertInchesToTwip(1),
              bottom: convertInchesToTwip(1),
              left: convertInchesToTwip(1),
              right: convertInchesToTwip(1),
            },
          },
        },
        children,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, resolvedFilename);
}
